import { Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { FloatLabelType, MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';

export interface FilterMultiSelectOption<T = any> {
  value: T;
  label: string;
}

// Multi-select with no "All" option that always keeps one option checked; all checked = don't filter.
@Component({
  selector: 'app-filter-multi-select',
  templateUrl: './filter-multi-select.component.html',
  styleUrls: ['./filter-multi-select.component.scss'],
  imports: [MatFormField, MatLabel, MatSelect, MatSelectTrigger, MatOption, MatTooltip, ReactiveFormsModule]
})
export class FilterMultiSelectComponent<T = any> {

  @Input() label = '';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() floatLabel: FloatLabelType = 'always';
  @Input() tooltipPosition: TooltipPosition = 'above';

  private _options: FilterMultiSelectOption<T>[] = [];
  @Input() set options(options: FilterMultiSelectOption<T>[]) {
    this._options = options ?? [];
    this.control.setValue(this.control.value.filter(v => this.isOption(v)));
  }

  get options(): FilterMultiSelectOption<T>[] {
    return this._options;
  }

  // Emitted when the panel closes.
  @Output() valueChanged = new EventEmitter<T[]>();

  readonly control = new FormControl<T[]>([], { nonNullable: true });
  private lastValue: T[] = [];

  constructor() {
    // Unchecking the last checked option puts it back.
    this.control.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      if (value.length == 0 && this.lastValue.length > 0) {
        this.control.setValue(this.lastValue);
        return;
      }

      this.lastValue = value;
    });
  }

  values(): T[] {
    return this.control.value;
  }

  allSelected(): boolean {
    return this.options.length > 0 && this.options.every(o => this.isSelected(o));
  }

  // [] or null checks every option.
  setValues(values: T[] | null) {
    const valid = (values ?? []).filter(v => this.isOption(v));
    this.control.setValue(valid.length > 0 ? valid : this.options.map(o => o.value));
  }

  selectedLabels(): string[] {
    return this.options.filter(o => this.isSelected(o)).map(o => o.label);
  }

  selectedLabel(): string {
    return this.allSelected() ? 'All' : this.selectedLabels().join(', ');
  }

  // Closed-field text: "Yes", "All", or "99499 +1".
  summary(): string {
    const labels = this.selectedLabels();

    if (labels.length <= 1) {
      return labels[0] ?? '';
    }

    return this.allSelected() ? 'All' : `${labels[0]} +${labels.length - 1}`;
  }

  isLocked(option: FilterMultiSelectOption<T>): boolean {
    return this.control.value.length == 1 && this.isSelected(option);
  }

  onClosed() {
    this.valueChanged.emit(this.control.value);
  }

  private isSelected(option: FilterMultiSelectOption<T>): boolean {
    return this.control.value.includes(option.value);
  }

  private isOption(value: T): boolean {
    return this._options.some(o => o.value === value);
  }
}
